import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Search,
  Calendar,
  Phone,
  Loader2,
  ArrowRight
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

export default function SalonCustomersPage() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, upcoming, past

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/salon/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
      }
    } catch (error) {
      toast.error('Müşteriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Filtreleme
  const now = new Date();
  const filteredCustomers = customers
    .filter(c => {
      // Arama
      const searchLower = searchTerm.toLowerCase();
      const matchSearch = !searchTerm || 
        c.bride_name?.toLowerCase().includes(searchLower) ||
        c.groom_name?.toLowerCase().includes(searchLower) ||
        c.bride_phone?.includes(searchTerm);
      
      // Tarih filtresi
      const eventDate = new Date(c.event_date);
      if (filter === 'upcoming') return matchSearch && eventDate >= now;
      if (filter === 'past') return matchSearch && eventDate < now;
      return matchSearch;
    })
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salon-customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold">Müşteriler</h1>
        <Link to="/salon/musteriler/yeni">
          <Button data-testid="new-customer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Rezervasyon
          </Button>
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="İsim veya telefon ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            Tümü ({customers.length})
          </Button>
          <Button
            variant={filter === 'upcoming' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('upcoming')}
          >
            Yaklaşan
          </Button>
          <Button
            variant={filter === 'past' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('past')}
          >
            Geçmiş
          </Button>
        </div>
      </div>

      {/* Customer List */}
      {filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'Aramayla eşleşen müşteri bulunamadı.' : 'Henüz müşteri kaydı yok.'}
            </p>
            {!searchTerm && (
              <Link to="/salon/musteriler/yeni">
                <Button className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Müşteriyi Ekle
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCustomers.map((customer) => {
            const isPast = new Date(customer.event_date) < now;
            return (
              <Link
                key={customer.id}
                to={`/salon/musteriler/${customer.id}`}
                className="block"
              >
                <Card className={`hover:shadow-md transition-shadow ${isPast ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                          isPast ? 'bg-muted' : 'bg-primary/10'
                        }`}>
                          <Calendar className={`h-6 w-6 ${isPast ? 'text-muted-foreground' : 'text-primary'}`} />
                        </div>
                        <div>
                          <p className="font-semibold">
                            {customer.bride_name} & {customer.groom_name}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(customer.event_date)}
                            </span>
                            {customer.venue_name && (
                              <span className="text-xs bg-primary/10 px-2 py-0.5 rounded">
                                {customer.venue_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {customer.phone1 || customer.bride_phone}
                            </span>
                            {customer.guest_count && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {customer.guest_count} Kişi
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-primary">{formatCurrency(customer.total_price || 0)}</p>
                          {customer.remaining_amount > 0 && (
                            <p className="text-xs text-orange-500">
                              Kalan: {formatCurrency(customer.remaining_amount)}
                            </p>
                          )}
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
